import React, { useState } from "react";
import { useConfirm } from "../../../ui/components/ConfirmDialog";
import { useTransitionAdminSeasonMutation } from "../api/gameApi";
import type { AdminSeason, AdminSeasonStatus } from "../api/types";
import { errorText, PHASE_LABEL, STATUS_ACTION, transitionConfirm } from "./adminText";
import { Btn, Notice, Row, Section, SectionTitle, Small } from "./adminStyles";

/** One button per move the backend allows, each behind a confirm that says what happens. */
const LifecyclePanel: React.FC<{ season: AdminSeason }> = ({ season }) => {
  const confirm = useConfirm();
  const [transition, { isLoading }] = useTransitionAdminSeasonMutation();
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const move = async (to: AdminSeasonStatus) => {
    setError(null);
    setDone(null);
    const c = transitionConfirm(season, to);
    const ok = await confirm({ title: c.title, message: c.message, confirmLabel: c.confirmLabel, tone: c.tone });
    if (!ok) return;
    try {
      const res = await transition({ id: season.id, status: to }).unwrap();
      setDone(`The season is now ${PHASE_LABEL[res.phase] ?? res.phase}.`);
    } catch (err) {
      setError(errorText(err, "Could not move the season."));
    }
  };

  return (
    <Section aria-label="Lifecycle">
      <SectionTitle>Lifecycle</SectionTitle>
      <Small style={{ marginBottom: 12 }}>
        The dates move the season on their own (open at the opening time, running from the first race, ended after the
        last). Use these to move it by hand. Moves only go forward.
      </Small>
      {season.allowedTransitions.length === 0 ? (
        <Small>No moves left: the season is archived.</Small>
      ) : (
        <Row>
          {season.allowedTransitions.map((to) => (
            <Btn
              key={to}
              type="button"
              variant={to === "ended" || to === "archived" ? "danger" : "primary"}
              disabled={isLoading}
              onClick={() => void move(to)}
            >
              {STATUS_ACTION[to] ?? to}
            </Btn>
          ))}
        </Row>
      )}
      {error && (
        <Notice tone="error" role="alert">
          {error}
        </Notice>
      )}
      {done && (
        <Notice tone="ok" role="status">
          {done}
        </Notice>
      )}
    </Section>
  );
};

export default LifecyclePanel;
